import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { DollarSign, Loader2, Save, Check, X, Crown, Award, Medal, Info, CreditCard } from "lucide-react";
import { getErrorMessage } from "@/lib/utils";

const TIER_ORDER = ["BRONZE", "SILVER", "GOLD"];

const TIER_ICONS = {
  BRONZE: Medal,
  SILVER: Award,
  GOLD: Crown,
};

const TIER_COLORS = {
  BRONZE: "text-amber-700",
  SILVER: "text-slate-400",
  GOLD: "text-yellow-500",
};

const DURATION_LABELS = {
  "1_month": "1 Month",
  "3_months": "3 Months",
  "6_months": "6 Months",
  "12_months": "12 Months",
};

const ALL_DURATIONS = ["1_month", "3_months", "6_months", "12_months"];

const FEATURE_LABELS = {
  cpm_enabled: "Views (CPM)",
  cpc_enabled: "Clicks (CPC)",
  cpdc_enabled: "Delivered Customers (CPDC)",
  deals_enabled: "Deals & Offers",
  basic_targeting: "Basic Targeting (Location, Time, Gender, Age, Day)",
  advanced_targeting: "Advanced Targeting (Venue Type, Interests)",
  animated_creatives: "Animated Creatives",
  competitor_targeting: "Competitor Targeting",
  ai_engine: "AI Engine",
  pause_enabled: "Campaign Pause",
};

// Global event-pricing rate fields. Keys match GlobalPricingSettings on the
// backend (backend/models/campaigns.py:GlobalPricingSettings). Per-1000
// impressions is the only field that needs special display treatment.
const GLOBAL_PRICING_FIELDS = [
  { key: "cpm_rate", label: "CPM — per 1000 impressions", help: "Charged per 1000 ad views (e.g. 120 = $0.12/view)." },
  { key: "cpc_rate", label: "CPC — per click", help: "Charged each time a passenger clicks the ad." },
  { key: "cpdc_direct_rate", label: "CPDC Direct — re-route accepted (50m)", help: "Passenger clicked then arrived within 50m." },
  { key: "cpdc_proximity_rate", label: "CPDC Proximity — saw ad recently (250m)", help: "Passenger saw the ad within 30 min and arrived within 250m." },
  { key: "cpdc_prompted_rate", label: "CPDC Prompted — AI suggestion accepted", help: "Passenger accepted an AI re-route suggestion." },
];

export default function TierPricing() {
  const { token } = useAuth();
  const [tierConfig, setTierConfig] = useState(null);
  const [globalPricing, setGlobalPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiersChanged, setTiersChanged] = useState(false);
  const [pricingChanged, setPricingChanged] = useState(false);
  const [stripeNoteOpen, setStripeNoteOpen] = useState(true);

  const hasChanges = tiersChanged || pricingChanged;

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tierRes, pricingRes] = await Promise.all([
        axios.get(`${API}/settings/tier-config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/pricing`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTierConfig(tierRes.data.tiers);
      setGlobalPricing(pricingRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data, "Failed to load pricing configuration"));
    } finally {
      setLoading(false);
    }
  };

  const updateTierField = (tierKey, field, value) => {
    setTierConfig((prev) => ({
      ...prev,
      [tierKey]: { ...prev[tierKey], [field]: value },
    }));
    setTiersChanged(true);
  };

  const updateGlobalPricing = (field, value) => {
    setGlobalPricing((prev) => ({ ...prev, [field]: parseFloat(value) || 0 }));
    setPricingChanged(true);
  };

  const updateDurationPrice = (tierKey, duration, value) => {
    setTierConfig((prev) => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        pricing: { ...prev[tierKey].pricing, [duration]: parseFloat(value) || 0 },
      },
    }));
    setHasChanges(true);
  };

  const toggleDuration = (tierKey, duration) => {
    setTierConfig((prev) => {
      const current = prev[tierKey].allowed_durations || [];
      const updated = current.includes(duration)
        ? current.filter((d) => d !== duration)
        : [...current, duration];
      return {
        ...prev,
        [tierKey]: { ...prev[tierKey], allowed_durations: updated },
      };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const requests = [];
      if (tiersChanged) {
        requests.push(
          axios.put(
            `${API}/settings/tier-config`,
            { tiers: tierConfig },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
      }
      if (pricingChanged) {
        requests.push(
          axios.put(
            `${API}/settings/pricing`,
            globalPricing,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
      }
      await Promise.all(requests);
      toast.success("Pricing updated successfully");
      setTiersChanged(false);
      setPricingChanged(false);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data, "Failed to save pricing"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tierConfig) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Campaign Tier Pricing
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure pricing, limits, and descriptions for each campaign tier. Feature flags are structural and cannot be changed here.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Stripe notes panel — explains what (if anything) needs changing
          in the Stripe console after editing prices. Collapsible. */}
      {stripeNoteOpen && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-2 text-sm">
                <p className="font-medium">Stripe console — no changes required</p>
                <p className="text-muted-foreground">
                  Pricing changes here take effect on the next campaign save.
                  We use one-time PaymentIntents calculated on the fly
                  (<code className="text-xs bg-muted px-1 py-0.5 rounded">tier_monthly × duration_months</code>),
                  not Stripe Subscriptions or stored Price IDs — so editing tier or event
                  pricing here does <span className="font-medium">not</span> require any
                  matching change in the Stripe dashboard.
                </p>
                <p className="text-xs text-muted-foreground">
                  Exception: if we later add a recurring add-on backed by a Stripe Product
                  (e.g. the Sponsored Trip Add-on), we'll mirror those prices in Stripe and
                  update this note.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setStripeNoteOpen(false)} className="h-7 px-2">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Global Event Pricing — CPM / CPC / CPDC rates that apply across all tiers.
          Backed by GET/PUT /settings/pricing (backend/routes/billing.py:48-74). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            Global Event Pricing
          </CardTitle>
          <CardDescription>
            Per-event rates that apply to every campaign. Changes here affect what advertisers see in their campaign pricing panel and what the ledger charges per event.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {globalPricing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {GLOBAL_PRICING_FIELDS.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">{field.label}</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={globalPricing[field.key] ?? ""}
                    onChange={(e) => updateGlobalPricing(field.key, e.target.value)}
                  />
                  <p className="text-[10px] text-muted-foreground/80">{field.help}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Global pricing not loaded.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TIER_ORDER.map((tierKey) => {
          const config = tierConfig[tierKey];
          if (!config) return null;
          const TierIcon = TIER_ICONS[tierKey];

          return (
            <Card key={tierKey} className="relative">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <TierIcon className={`h-5 w-5 ${TIER_COLORS[tierKey]}`} />
                  {config.name}
                </CardTitle>
                <CardDescription>
                  {config.is_free ? "Free tier" : "Paid tier"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Monthly Price */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Monthly Price ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={config.price_monthly}
                    onChange={(e) => updateTierField(tierKey, "price_monthly", parseFloat(e.target.value) || 0)}
                  />
                </div>

                {/* Duration Pricing */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration Pricing (Total $)</Label>
                  <div className="space-y-2">
                    {ALL_DURATIONS.map((dur) => (
                      <div key={dur} className="flex items-center gap-2">
                        <span className="text-sm w-24 text-muted-foreground">{DURATION_LABELS[dur]}</span>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="flex-1"
                          value={config.pricing?.[dur] ?? ""}
                          onChange={(e) => updateDurationPrice(tierKey, dur, e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Allowed Durations */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Allowed Durations</Label>
                  <div className="flex flex-wrap gap-2">
                    {ALL_DURATIONS.map((dur) => {
                      const checked = config.allowed_durations?.includes(dur);
                      return (
                        <label key={dur} className="flex items-center gap-1.5 cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleDuration(tierKey, dur)}
                          />
                          <span className="text-sm">{DURATION_LABELS[dur]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Max Campaigns */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Max Campaigns per Account</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={config.max_campaigns_per_account}
                    onChange={(e) => updateTierField(tierKey, "max_campaigns_per_account", parseInt(e.target.value) || 1)}
                  />
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Description</Label>
                  <Input
                    value={config.description || ""}
                    onChange={(e) => updateTierField(tierKey, "description", e.target.value)}
                    placeholder="Tier description..."
                  />
                </div>

                {/* Tagline */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tagline</Label>
                  <Input
                    value={config.tagline || ""}
                    onChange={(e) => updateTierField(tierKey, "tagline", e.target.value)}
                    placeholder="Short tagline..."
                  />
                </div>

                {/* Enabled — controls whether advertisers can select this
                    tier when creating a campaign. Disabled tiers stay
                    visible in the picker but appear greyed out with a
                    "Coming Soon" badge. */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Availability</Label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={config.enabled !== false}
                      onCheckedChange={(checked) => updateTierField(tierKey, "enabled", checked === true)}
                      className="mt-0.5"
                    />
                    <span className="text-sm">
                      Enabled for selection
                      <span className="block text-[11px] text-muted-foreground">
                        Disabled tiers stay visible to users as "Coming Soon".
                      </span>
                    </span>
                  </label>
                </div>

                {/* Features (read-only) */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Features (read-only)</Label>
                  <div className="space-y-1">
                    {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                      const enabled = config.features?.[key];
                      if (enabled === undefined) return null;
                      return (
                        <div key={key} className={`flex items-center gap-2 text-sm ${enabled ? "" : "text-muted-foreground"}`}>
                          {enabled ? (
                            <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />
                          )}
                          <span>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
